import { useCallback, useMemo, useState } from 'react';
import { Flex } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { useParams, useSearchParams } from 'react-router-dom';

import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { MAX_SEARCH_LENGTH } from '@duke-hq/shared-constants/searchCaps';
import { TicketList, TicketsPageHeader, TicketDetailsArea } from '@duke-hq/svc-support-web';
import FilterBarLayout from '@duke-hq/filters/components/FilterBarLayout';
import FiltersProvider from '@duke-hq/filters/context/FiltersProvider';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { TicketResolution, TicketStatus } from '@duke-hq/svc-api-db';

import { useAuthAdmin } from '../AuthProviderAdmin';
import { sizeVars } from '@duke-hq/ui/constants/layout';
import { ProviderFilter } from './filters/ProviderFilter';
import { AssignedToFilter } from './filters/AssignedToFilter';
import { SupportTypeFilter } from './filters/SupportTypeFilter';
import { StatusFilter } from './filters/StatusFilter';
import { useChatActions } from './useChatActions';
import { useCheckPermission } from '../hooks/useCheckPermission';
import { AssignAdminDropdown } from '../components/AssignAdminDropdown';
import { TicketStatusBar } from './components/TicketStatusBar';
import { pxToRem } from '@duke-hq/ui/constants/scalable';

function SupportTicketsContent() {
  const [searchQuery, setSearchQuery] = useState('');
  const { filters, updateFilters } = useFiltersContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'archived' ? 'archived' : 'open';
  const { ticketId } = useParams();
  const session = useAuthAdmin();
  const hasManagePermission = useCheckPermission('canManageSupportRequests');
  const trpcUtils = trpcAdmin.useUtils();

  const statusParam = useMemo(() => {
    if (activeTab === 'archived') {
      return [TicketStatus.CLOSED];
    }

    return filters.ticketStatus;
  }, [filters.ticketStatus, activeTab]);

  const {
    data: ticketListData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = trpcAdmin.support.ticket.list.useInfiniteQuery(
    {
      searchQuery,
      status: statusParam?.join(','),
      providerId: filters.provider?.toString(),
      studentOnly: filters.studentOnly === 'true' ? true : undefined,
      assignedToDrn: filters.assignedTo,
      supportType: filters.supportType,
    },
    {
      getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,
    },
  );

  const { data: selectedTicketData } = trpcAdmin.support.ticket.get.useQuery(
    { ticketId: ticketId ?? '' },
    { enabled: ticketId !== undefined },
  );

  const assignTicketMutation = trpcAdmin.support.ticket.assign.useMutation({
    onSuccess: () => {
      void trpcUtils.support.ticket.get.invalidate({
        ticketId: ticketId ?? '',
      });
      void trpcUtils.support.ticket.list.invalidate();
      void trpcUtils.notifications.unreadTicketCount.invalidate();
    },
  });

  const updateStatusMutation = trpcAdmin.support.ticket.updateStatus.useMutation({
    onSuccess: () => {
      void trpcUtils.support.ticket.get.refetch({ ticketId: ticketId ?? '' });
      void trpcUtils.support.ticket.list.refetch();
      void trpcUtils.notifications.unreadTicketCount.invalidate();
    },
  });

  const handleAssignmentChange = (assignedToDrn: string | null) => {
    if (ticketId !== undefined) {
      assignTicketMutation.mutate({
        ticketId,
        assignedToDrn,
      });
    }
  };

  const handleStatusChange = (params: { status: TicketStatus; resolution?: TicketResolution }) => {
    if (ticketId !== undefined) {
      updateStatusMutation.mutate({ ticketId, ...params });
    }
  };

  const handleMarkResolved = () => {
    if (ticketId !== undefined) {
      updateStatusMutation.mutate({
        ticketId,
        status: TicketStatus.CLOSED,
        resolution: TicketResolution.SOLVED,
      });
    }
  };

  const chatActions = useChatActions(selectedTicketData?.chatUsers ?? {}, ticketId);

  const tickets = useMemo(
    () => ticketListData?.pages.flatMap(({ tickets }) => tickets) ?? [],
    [ticketListData?.pages],
  );

  const providers = ticketListData?.pages[0]?.providers ?? [];
  const tabCount = ticketListData?.pages[0]?.tabCount ?? 0;

  const handleEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const updateTabInSearch = useCallback(
    (tab: string) => {
      const next = new URLSearchParams(searchParams);

      if (tab === 'archived') {
        next.set('tab', 'archived');
      } else {
        next.delete('tab');
      }

      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const tabs = [
    {
      key: 'open',
      label: <Trans>All</Trans>,
      count: activeTab === 'open' ? tabCount : undefined,
      isActive: activeTab === 'open',
      onClick: () => {
        updateTabInSearch('open');
        updateFilters({ ticketStatus: [] });
      },
    },
    {
      key: 'archived',
      label: <Trans>Archived</Trans>,
      count: activeTab === 'archived' ? tabCount : undefined,
      isActive: activeTab === 'archived',
      onClick: () => {
        updateTabInSearch('archived');
        updateFilters({ ticketStatus: [] });
      },
    },
  ];

  return (
    <Flex direction="column" w="100%" h={`calc(100vh - ${sizeVars.navbar})`}>
      <TicketsPageHeader>
        <SearchInput
          maxW={pxToRem(200)}
          onCommit={setSearchQuery}
          urlSync={false}
          maxLength={MAX_SEARCH_LENGTH}
        />

        <FilterBarLayout>
          <ProviderFilter key="provider" providers={providers} />
          <AssignedToFilter key="assigned" />
          <SupportTypeFilter key="supportType" />
          <StatusFilter key="status" />
        </FilterBarLayout>
      </TicketsPageHeader>

      <Flex flex={1} overflow="hidden">
        <TicketList
          loading={isLoading}
          tabs={tabs}
          tickets={tickets}
          baseUrl="/support"
          selectedTicketId={ticketId}
          showAssignedTo
          hasMore={hasNextPage ?? false}
          isFetchingMore={isFetchingNextPage}
          onEndReached={handleEndReached}
        />
        <TicketDetailsArea
          ticket={selectedTicketData?.ticket}
          currentUserDrn={session.user?.drn}
          chatActions={chatActions}
          headerActions={
            <AssignAdminDropdown
              currentAssignee={selectedTicketData?.ticket.currentAssignee ?? null}
              onAssign={handleAssignmentChange}
              selfAssignOnly={!hasManagePermission}
            />
          }
          statusBar={
            selectedTicketData?.ticket ? (
              <TicketStatusBar
                ticket={selectedTicketData.ticket}
                onStatusChange={handleStatusChange}
                onMarkResolved={handleMarkResolved}
                isLoading={updateStatusMutation.isPending}
              />
            ) : undefined
          }
        />
      </Flex>
    </Flex>
  );
}

export function SupportTicketsPage() {
  return (
    <FiltersProvider localStorageKey="support-tickets-filters">
      <SupportTicketsContent />
    </FiltersProvider>
  );
}
